import React, {
  useEffect,
  useRef,
  useState,
} from "react";
import ActaModal from "@/components/ui/ActaModal";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";


export default function MailSignatureDialog({
  open = true,
  initialValue = "",
  initialAutoEnabled = false,
  saving = false,
  onClose,
  onSubmit,
  embedded = false,
}) {
  const [
    value,
    setValue,
  ] = useState("");
  const [
    autoEnabled,
    setAutoEnabled,
  ] = useState(false);
  const inputRef = useRef(null);

  useEffect(
    () => {
      if (!open) {
        return;
      }

      setValue(
        String(initialValue || "")
      );
      setAutoEnabled(
        Boolean(initialAutoEnabled)
      );

      window.setTimeout(
        () => inputRef.current?.focus(),
        0
      );
    },
    [
      open,
      initialValue,
      initialAutoEnabled,
    ]
  );

  if (!open) {
    return null;
  }

  function submit(event) {
    event.preventDefault();

    if (saving) {
      return;
    }

    onSubmit(
      value,
      autoEnabled
    );
  }

  const formContent = (
    <form
      className="space-y-5"
      onSubmit={submit}
      data-testid="mail-signature-form"
    >
      <div>
        <p className="text-sm leading-6 text-[var(--do-muted)]">
          Saisissez la signature associée à votre compte
          et choisissez si Acta doit l’ajouter automatiquement.
        </p>

        <label
          htmlFor="mail-signature-text"
          className="mt-4 block"
        >
          <span className="mb-2 block text-sm font-black text-[var(--do-ink)]">
            Signature
          </span>

          <Textarea
            ref={inputRef}
            id="mail-signature-text"
            value={value}
            maxLength={5000}
            disabled={saving}
            rows={10}
            placeholder={"Ex. Prénom Nom\nFonction\nEntreprise\nTéléphone"}
            onChange={(event) =>
              setValue(
                event.target.value
              )
            }
            data-testid="mail-signature-text"
          />

          <span className="mt-1 block text-right text-xs text-[var(--do-muted)]">
            {value.length}/5000
          </span>
        </label>

        <p className="mt-2 text-xs leading-5 text-[var(--do-muted)]">
          Laissez le champ vide puis enregistrez pour effacer la signature.
        </p>

        <div className="mt-5 flex items-center justify-between gap-4 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-2)] px-4 py-3">
          <div>
            <p className="text-sm font-black text-[var(--do-ink)]">
              Ajouter automatiquement cette signature
            </p>
            <p className="mt-1 text-xs leading-5 text-[var(--do-muted)]">
              La signature sera ajoutée aux nouveaux messages, réponses et transferts.
            </p>
          </div>

          <Switch
            checked={autoEnabled}
            disabled={saving}
            onCheckedChange={setAutoEnabled}
            aria-label="Ajouter automatiquement la signature"
            data-testid="mail-signature-auto-toggle"
          />
        </div>
      </div>

      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        {!embedded && (
          <Button
            type="button"
            variant="actaSecondary"
            disabled={saving}
            onClick={onClose}
          >
            Annuler
          </Button>
        )}

        <Button
          type="submit"
          variant="actaPrimary"
          disabled={saving}
          data-testid="mail-signature-confirm"
        >
          {saving
            ? "Enregistrement…"
            : "Enregistrer"}
        </Button>
      </div>
    </form>

  );

  if (embedded) {
    return (
      <div data-testid="mail-signature-settings">
        {formContent}
      </div>
    );
  }

  return (
    <ActaModal
      open
      onClose={() => {
        if (!saving) {
          onClose?.();
        }
      }}
      title="Ma signature mail"
    >
      {formContent}
    </ActaModal>
  );
}
